<template>
  <div class="h-100 d-flex">
    <div class="overflow-scroll p-1 w-100">
      <div class="d-flex align-items-center mb-1">
        <div class="d-flex w-100 align-items-center">
          <b-button variant="outline-secondary" class="size-button-center" @click="$emit('slide_window')">
             <span><feather-icon icon="MenuIcon"/></span>
          </b-button>
        </div>
        
        <b-button 
          variant="outline-secondary" 
          class="h-100 ml-1 d-xl-none"
          @click="show_favorites = true"
        >
          <feather-icon icon="SettingsIcon"></feather-icon>
        </b-button>
      </div>

      <div>
        <h4>{{ $t('ia.assistants.assistants') }}</h4>
        <p class="avenir-medium">{{ $t('ia.assistants.assistantsDescription') }}</p>

        <!-- Loading Skeletons -->
        <div v-if="loading" class="assistants-grid">
          <AssistantCardSkeleton 
            v-for="n in 8" 
            :key="'skeleton-' + n"
          />
        </div>

        <!-- Actual Assistants Grid -->
        <div v-else class="assistants-grid">
          <AssistantCard
            v-for="assistant in assistantsComputed" 
            :key="assistant.id"
            :assistant="assistant"
            @card-click="$emit('assistant-selected', assistant)"
            @toggle-favorite="toggleFavorite"
          />
        </div>

        <!-- Empty State -->
        <div v-if="!loading && assistantsComputed.length === 0" class="empty-state text-center py-5">
          <feather-icon icon="InboxIcon" size="48" class="text-muted mb-3"></feather-icon>
          <h5 class="text-muted">{{ $t('ia.assistants.noAssistantsFound') }}</h5>
          <p class="text-muted">{{ $t('ia.assistants.tryDifferentSearch') }}</p>
        </div>
      </div>

      
    </div>
    <div class="width-right-area card m-0 d-none d-xl-block">
      <sider-favorites-assistants
        :favorites="favoritesList"
        :all_assistants="assistantsComputed"
        @reorder-favorites="onReorderFavorites"
        @remove-favorite="onRemoveFavorite"
        @add-favorite-slot="onAddFavoriteSlot"
        @favorite-click="selectAssistant"
      />
    </div>

    <!-- Sidebar favorites -->
    <b-sidebar right v-model="show_favorites" class="sidebar-filters-brandme-ai" shadow backdrop>
      <sider-favorites-assistants
        :favorites="favoritesList"
        :all_assistants="assistantsComputed"
        @reorder-favorites="onReorderFavorites"
        @remove-favorite="onRemoveFavorite"
        @add-favorite-slot="onAddFavoriteSlot"
        @favorite-click="selectAssistant"
      />
    </b-sidebar>
  </div>
</template>
    
<script>

import {
  BButton,
  BSidebar,
} from 'bootstrap-vue';
import { showToast } from '@/libs/utils/others';
import { updateSettings } from '@/services/ia';
    
export default {
  name: 'ExploreAssistants',
  components: {
    BButton,
    BSidebar,
    AssistantCard: () => import('@/views/components/brandme-ai/cards/AssistantCard.vue'),
    AssistantCardSkeleton: () => import('@/views/components/brandme-ai/cards/AssistantCardSkeleton.vue'),
    siderFavoritesAssistants: () => import('@/views/components/brandme-ai/siderbars/siderFavoritesAssistants.vue')
  },
  props: {
    data_settings: {
      type: Object,
      default: null,
    }
  },
  data() {
    const DEFAULT_FILTERS = {
      formalities: ['formal','casual'],
      bias: ['neutral','opinionated'],
      response_length: ['short','long']
    };
    
    return {
      loading: true,
      query: undefined,
      show_favorites: false,
      current_filters: { ...DEFAULT_FILTERS },
      fetch_timeout: null,
      DEFAULT_FILTERS
    }
  },
  
  mounted() {
    setTimeout(() => {
      this.loading = false;
    }, 500);
  },
  
  beforeDestroy() {
    if (this.fetch_timeout) {
      clearTimeout(this.fetch_timeout);
    }
  },
  
  methods: {
    toggleFavorite(assistant) {
      if (!this.data_settings) return;
      const current = (this.data_settings.favorites_options || []).slice();
      const exists = current.find(f => f.slug === assistant.slug);

      let new_favorites = [];
      if (exists) {
        new_favorites = current.filter(f => f.slug !== assistant.slug);
        showToast(this, 'info', 'HeartIcon', 'ia.assistants.removedFromFavorites');
      } else {
        if (current.length >= 3) {
          showToast(this, 'warning', 'AlertTriangleIcon', 'ia.assistants.maxFavoritesReached');
          return;
        }
        new_favorites = [...current, { name: assistant.slug, slug: assistant.slug, order: current.length }];
        showToast(this, 'success', 'HeartIcon', 'ia.assistants.addedToFavorites');
      }

      new_favorites = new_favorites.map((f, idx) => ({ ...f, order: idx }));
      this.updateSettings({ favorites: new_favorites });
    },

    updateSettings(data) {
      updateSettings(data).then((response) => {
        this.$emit('update-settings', response);
      }).catch(() => {
        showToast(this, 'error', 'XCircleIcon', this.$t('ia.settings.updateFailed'));
      });
    },
    
    selectAssistant(assistant) {
      this.$emit('assistant-selected', assistant);
      showToast(this, 'info', 'UserIcon', `Selected: ${assistant.name}`);
    },
    
    onApplyFilters(finalFilters) {
      this.current_filters = { ...finalFilters };
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 1000);
      this.show_favorites = false;
    },
    onReorderFavorites(newOrder) {
      this.updateSettings({ favorites: newOrder });
    },
    onRemoveFavorite(fav) {
      const current = (this.data_settings?.favorites_options || []).filter(f => f.slug !== fav.slug);
      const reordered = current.map((f, idx) => ({ ...f, order: idx }));
      this.updateSettings({ favorites: reordered });
    },
    onAddFavoriteSlot() {
      showToast(this, 'info', 'PlusIcon', 'ia.assistants.selectAssistantToAdd');
    }
  },
  
  watch: {
    query(new_val, old_val) {
      if (this.fetch_timeout) {
        clearTimeout(this.fetch_timeout);
      }
      
      if (new_val !== old_val) {
        this.fetch_timeout = setTimeout(() => {
          this.loading = true;
          setTimeout(() => {
            this.loading = false;
          }, 800);
        }, 450);
      }
    }
  },
  computed: {
    favoritesList() {
      return (this.data_settings && this.data_settings.favorites_options) ? this.data_settings.favorites_options : [];
    },
    assistantsComputed() {
      const catalog = this.$t('ia.assistants.catalog');
      const assistants = Object.keys(catalog).map(slug => {
        const assistant = catalog[slug];
        return {
          ...assistant,
          isFavorite: this.favoritesList.some(f => f.slug === slug)
        };
      });
      return assistants;
    }
  }
}
</script>

<style lang="scss" scoped>
.overflow-scroll {
  max-height: 100%;
  overflow-y: auto;
}

.width-right-area {
  width: 400px;
  flex-shrink: 0;
}

.assistants-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
}

.empty-state {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  border: 1px solid #e0e0e0;
}
</style>

<style lang="scss">
.sidebar-filters-brandme-ai {
  .b-sidebar { 
    width: 400px !important;
    background-color: white !important;
  }
}
</style>